/**
 * "/b/:boardId" — compact board controls above the canvas. The name, stage
 * and Music disclosure share a card; the board tools sit beside it. Shorts
 * and pen controls each get their own row so all controls remain reachable. Undo/redo, the Grid/Script
 * segmented toggle, and (in Script mode) a page-width slider live in the left
 * pill's "⋯" menu; zoom has no on-screen control (trackpad/mouse only —
 * ctrl/cmd+wheel or pinch, see BoardCanvas). Every icon-only control in the
 * pills carries a `data-tip` hover label (app.css): an icon alone does not
 * say what it does, and the native `title` bubble is too slow and too easy
 * to miss over a canvas. Also renders the F-45 banner and the F-121
 * board-first canvas (dot grid + framed grid + floating elements).
 *
 * F-13 (amended 2026-07-10; revised 2026-07-15): row/column ADD affordances
 * live on the grid itself — the F-12b hover pill's both-sides "+" is the
 * primary path, with the gutter boundary bubbles and context menus as
 * secondary; the persistent edge "+" strips are REMOVED, and the old toolbar
 * +Row/+Column buttons are gone. F-21: exactly one grid per board; no sheet tabs.
 */
import {
  BOARD_STATUSES,
  BOARD_STATUS_LABELS,
  can,
  type BoardStatus,
  type BoardSummary,
  type ShortSlotPatch,
} from "@kola/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "zustand";

import {
  getBoard,
  renameBoard,
  setBoardScriptFinishDate,
  updateBoardPlan,
} from "../../core/api.js";
import { useMe } from "../../core/useMe.js";
import { createCanvasStore } from "../../core/canvas.js";
import { currentUser } from "../../core/identity.js";
import { isMac } from "../../core/keyboard.js";
import { BoardSession } from "../../core/session.js";
import {
  boardShareLink,
  boardViewShareLink,
  CELL_PARAM,
  copyToClipboard,
  ELEMENT_PARAM,
  VIEW_PARAM,
} from "../../core/share-link.js";
import { useIsNarrowContainer } from "../../core/viewport.js";
import {
  iconBack,
  iconDesign,
  iconThumbnail,
  iconDots,
  iconExport,
  iconGrid,
  iconImport,
  iconLink,
  iconMusic,
  iconPanel,
  iconRedo,
  iconRowsClear,
  iconScript,
  iconSections,
  iconTeleprompter,
  iconSyncFolder,
  iconTranslate,
  iconUndo,
  iconAgent,
} from "../../ui/icons.js";
import { Modal } from "../../ui/Modal.js";
import { getHost, onBoardsChanged, setDocumentTitle } from "../../core/host.js";
import {
  storyboardSync,
  useStoryboardSyncEnabled,
  useStoryboardSyncStatus,
} from "../storyboard-sync/store.js";
import { useStoryboardSync } from "../storyboard-sync/useStoryboardSync.js";
import { ThemeToggle } from "../../ui/ThemeToggle.js";
import { NotificationsBell } from "../notifications/NotificationsBell.js";
import { ToastHost, showToast } from "../../ui/toast.js";
import { AgentPanel, AGENT_FUNCTION_KEY } from "../agent/AgentPanel.js";
import { loadImportTasks } from "../agent/import-tasks.js";
import { hasPendingImport } from "../agent/pending-import.js";
import { createAgentUiStore } from "../agent/store.js";
import { useAgentImportOnOpen } from "../agent/useAgentImportOnOpen.js";
import { storedModel } from "../ai-models/pick.js";
import { CommentsPanel } from "../comments/CommentsPanel.js";
import { DesignPanel } from "../design/DesignPanel.js";
import { createDesignUiStore } from "../design/store.js";
import { ThumbnailPanel } from "../thumbnails/ThumbnailPanel.js";
import { createThumbUiStore } from "../thumbnails/store.js";
import { useThumbnailDeepLink } from "../thumbnails/usePanelDeepLink.js";
import { createCommentsUiStore } from "../comments/store.js";
import { useThreadDeepLink } from "../comments/useThreadDeepLink.js";
import { AssignStatusDialog } from "../dashboard/AssignStatusDialog.js";
import { DateButton } from "../../ui/DateButton.js";
import { formatDateKey, isPastDateKey } from "../../ui/date-key.js";
import {
  StoryboardGateDialog,
  type StoryboardGateAction,
} from "../dashboard/StoryboardGateDialog.js";
import { DrawToolbar } from "../drawing/DrawToolbar.js";
import { createDrawUiStore } from "../drawing/store.js";
import { downloadCsv, sheetToCsv } from "../import-export/csv.js";
import { beginImport } from "../import-export/ImportScriptLayer.js";
import { IMPORT_ACCEPT } from "../import-export/script-file.js";
import { createImportUiStore, importBusy } from "../import-export/store.js";
import { ScriptView } from "../script/ScriptView.js";
import { scriptToTeleprompterText } from "../script/teleprompter.js";
import { bgmUi } from "../bgm/store.js";
import { sectionsUi } from "../sections/store.js";
import { loadAiModels } from "../ai-models/load.js";
import { loadPresenterPhotos } from "../presenter-photos/load.js";
import { loadTextKinds } from "../text-kinds/load.js";
import { translateUi } from "../translate/store.js";
import { cellRectFromLayout } from "../comments/anchoring.js";
import { fitCamera } from "../../core/canvas.js";
import { AvatarStack } from "./AvatarStack.js";
import { BoardCanvas } from "./BoardCanvas.js";
import { ConnectionBanner } from "./ConnectionBanner.js";
import { ShortsRail } from "./ShortsRail.js";

/** "Grid" (canvas) vs "Script" (Notion-style document of Original Script). */
type BoardViewMode = "grid" | "script";

function viewModeStorageKey(boardId: string): string {
  return `kola.viewMode.${boardId}`;
}

/** Script-mode page width (px) — a personal display preference, not per-board. */
const SCRIPT_WIDTH_STORAGE_KEY = "kola.scriptWidth";
const SCRIPT_WIDTH_MIN = 480;
const SCRIPT_WIDTH_MAX = 1200;
const SCRIPT_WIDTH_DEFAULT = 760;

function clampScriptWidth(width: number): number {
  return Math.min(SCRIPT_WIDTH_MAX, Math.max(SCRIPT_WIDTH_MIN, width));
}

export function BoardScreen() {
  const { boardId } = useParams<{ boardId: string }>();
  const [session, setSession] = useState<BoardSession | null>(null);
  const [summary, setSummary] = useState<BoardSummary | null>(null);
  const user = useMemo(() => currentUser(), []);

  useEffect(() => {
    if (boardId === undefined) return;
    const next = new BoardSession(boardId, user);
    setSession(next);
    // Refresh the two admin-defined registries on every board open: cheap,
    // and what makes a `/` kind or an AI model an admin added this morning
    // show up — styled, or in every model dropdown — without a reload.
    void loadTextKinds();
    void loadAiModels();
    void loadPresenterPhotos();
    return () => {
      setSession(null);
      next.destroy();
    };
  }, [boardId, user]);

  useEffect(() => {
    if (boardId === undefined) return;
    let cancelled = false;
    const load = () =>
      getBoard(boardId)
        .then((board) => {
          if (!cancelled) setSummary(board);
        })
        .catch(() => {
          /* name/stage are cosmetic here; the WS auth path reports real errors */
        });
    void load();
    // Name, stage and dates are REST-only: when another of the host's
    // windows moves them, fetch the summary again (host.ts `boardsChanged`).
    const stopBoardsChanged = onBoardsChanged(() => void load());
    return () => {
      cancelled = true;
      stopBoardsChanged();
    };
  }, [boardId]);

  const title = summary?.name ?? boardId ?? "Board";
  useEffect(() => {
    setDocumentTitle(`${title} — Kola`);
    return () => {
      setDocumentTitle("Kola");
    };
  }, [title]);

  if (boardId === undefined) {
    return <div className="grid-empty">Missing board id.</div>;
  }

  return (
    <div className="board-screen">
      {session !== null ? (
        <BoardView
          key={boardId}
          session={session}
          boardName={title}
          summary={summary}
          onSummaryChange={setSummary}
        />
      ) : (
        <div className="grid-empty">Opening board…</div>
      )}
      <ToastHost />
    </div>
  );
}

function BoardView({
  session,
  boardName,
  summary,
  onSummaryChange,
}: {
  session: BoardSession;
  boardName: string;
  summary: BoardSummary | null;
  onSummaryChange: (board: BoardSummary) => void;
}) {
  const navigate = useNavigate();
  const status = useStore(session.store, (s) => s.status);
  const frozen = useStore(session.store, (s) => s.frozen);
  const authError = useStore(session.store, (s) => s.authError);
  const peers = useStore(session.store, (s) => s.peers);
  const sheetId = useStore(session.store, (s) => s.sheetId);

  // Grid vs Script. An explicit choice (persisted per board) wins; otherwise
  // a board in the Scripting stage opens as the script document. F-150: a
  // board opening with a file to import opens on the GRID — the file lands
  // as a table, and the step tasks work on its columns.
  const [viewChoice, setViewChoice] = useState<BoardViewMode | null>(() => {
    try {
      if (hasPendingImport(session.boardId)) {
        localStorage.setItem(viewModeStorageKey(session.boardId), "grid");
        return "grid";
      }
      const stored = localStorage.getItem(viewModeStorageKey(session.boardId));
      return stored === "grid" || stored === "script" ? stored : null;
    } catch {
      return null;
    }
  });
  // F-123: a link to a view or an item names a place on the CANVAS, so it
  // opens the grid — even on a board that would open as the script document,
  // and even over a Script choice this reader made earlier — for this open
  // only: nothing is persisted, and the first view switch they make hands
  // control back. Read once at mount; the canvas honours the params
  // themselves (useShareDeepLink) and strips them.
  const [searchParams] = useSearchParams();
  const [linkWantsGrid, setLinkWantsGrid] = useState(
    () =>
      searchParams.has(VIEW_PARAM) ||
      searchParams.has(CELL_PARAM) ||
      searchParams.has(ELEMENT_PARAM),
  );
  // …and again for a link that arrives while the board is already open —
  // the bell, or the desktop host navigating its embedded Kola to an item —
  // which the mount-time read above cannot see.
  useEffect(() => {
    if (
      searchParams.has(VIEW_PARAM) ||
      searchParams.has(CELL_PARAM) ||
      searchParams.has(ELEMENT_PARAM)
    ) {
      setLinkWantsGrid(true);
    }
  }, [searchParams]);
  const viewMode: BoardViewMode = linkWantsGrid
    ? "grid"
    : (viewChoice ?? (summary?.status === "scripting" ? "script" : "grid"));
  const chooseView = (mode: BoardViewMode) => {
    setLinkWantsGrid(false);
    setViewChoice(mode);
    try {
      localStorage.setItem(viewModeStorageKey(session.boardId), mode);
    } catch {
      // Preference only.
    }
  };

  // Stage chip + change-stage menu (assignees required — the dialog).
  const [stageMenuOpen, setStageMenuOpen] = useState(false);
  const [stageTarget, setStageTarget] = useState<BoardStatus | null>(null);
  // F-133: the sign-off entries on the stage menu, for whoever may approve.
  // The one place the client keeps `role` — see core/useMe.ts.
  const { me } = useMe();
  const canApprove = me !== null && can(me.role, "review.approve");
  const [signingOff, setSigningOff] = useState<StoryboardGateAction | null>(null);

  /**
   * F-137: move the script deadline from the board's own top bar — the same
   * optimistic write as the dashboard card (Dashboard.handleSetScriptDue),
   * rolled back with a toast if the server refuses. A writer who opened the
   * board to work on the script should not have to leave it to re-plan it.
   */
  const handleSetScriptDue = async (dateKey: string) => {
    if (summary === null || dateKey === "" || dateKey === summary.scriptFinishDate) return;
    const previous = summary;
    onSummaryChange({ ...summary, scriptFinishDate: dateKey });
    try {
      onSummaryChange(await setBoardScriptFinishDate(summary.id, dateKey));
      showToast(`Script due ${formatDateKey(dateKey)} — the Scripting task follows.`);
    } catch (err) {
      onSummaryChange(previous);
      showToast(err instanceof Error ? err.message : "Couldn't move the script due date");
    }
  };
  /**
   * F-141: a Shorts week's plan moves from the rail — the same optimistic
   * write as the script date: the rail's own guess of the outcome paints at
   * once, the server's answer replaces it, a refusal rolls it back.
   */
  const handlePlanPatch = async (
    patches: ShortSlotPatch[],
    summaryAfter: BoardSummary,
    toast: string,
  ) => {
    if (summary === null || patches.length === 0) return;
    const previous = summary;
    onSummaryChange(summaryAfter);
    try {
      onSummaryChange(await updateBoardPlan(summary.id, { shorts: patches }));
      showToast(toast);
    } catch (err) {
      onSummaryChange(previous);
      showToast(err instanceof Error ? err.message : "Couldn't update the short");
    }
  };

  /**
   * F-141 "Jump to rows": frame a short's first row the way a `?cell=` link
   * frames its cell (useShareDeepLink) — the grid's layout is published into
   * the comments store for exactly this kind of outside reader.
   */
  const handleJumpToRow = (rowId: string) => {
    if (sheetId === null) return;
    const layout = commentsUi.getState().gridLayout;
    if (layout === null) return;
    const colId = session.board.sourceColumnId(sheetId) ?? layout.colIds[0];
    if (colId === undefined) return;
    const rect = cellRectFromLayout(layout, rowId, colId);
    if (rect === null) return;
    const state = canvas.getState();
    state.setCamera(fitCamera(rect, state.viewport, state.camera, { padding: 96 }));
  };
  // Double-click the title → rename dialog (needs the loaded summary for the
  // current name; the board id alone isn't renameable text).
  const [renamingBoard, setRenamingBoard] = useState(false);
  // Left pill's "⋯" menu (Miro-style) — houses back navigation, the
  // Grid/Script switch, undo/redo, import/export and "Delete empty rows", so
  // the floating pills themselves stay tiny. NOT here (2026-09-03): "Divide
  // into sections", which every board goes through and so earns an icon in
  // the right pill; and the BGM track, which expands from the Music button.
  // Stems are added in the BGM column's own cells (BgmSlotLayer). Closed on outside click (not
  // onBlur alone): the script-width slider below needs to keep focus while
  // dragged.
  const [menuTarget, setMenuTarget] = useState<HTMLDivElement | null>(null);
  const [topbarMenuOpen, setTopbarMenuOpen] = useState(false);
  const topbarMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!topbarMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        topbarMenuRef.current?.contains(event.target as Node) ||
        menuTarget?.contains(event.target as Node)
      )
        return;
      setTopbarMenuOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [topbarMenuOpen, menuTarget]);

  // Use the board's own width: the desktop host may have a wide window but
  // give Kola only a narrow pane. The toolbar rows wrap at every width.
  const headerRef = useRef<HTMLDivElement | null>(null);
  const narrow = useIsNarrowContainer(headerRef);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const toolsMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!toolsMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (
        toolsMenuRef.current?.contains(event.target as Node) ||
        menuTarget?.contains(event.target as Node)
      )
        return;
      setToolsMenuOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [toolsMenuOpen, menuTarget]);
  // Rotating to landscape (or widening a window) un-collapses the bar; the
  // menu that held those tools no longer has a button to hang off.
  useEffect(() => {
    if (!narrow) setToolsMenuOpen(false);
  }, [narrow]);

  // Script-mode page width (F-28 script view) — a personal preference
  // persisted across boards, adjustable via the "⋯" menu's slider.
  const [scriptWidth, setScriptWidthState] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(SCRIPT_WIDTH_STORAGE_KEY);
      const parsed = stored !== null ? Number(stored) : NaN;
      return Number.isFinite(parsed) ? clampScriptWidth(parsed) : SCRIPT_WIDTH_DEFAULT;
    } catch {
      return SCRIPT_WIDTH_DEFAULT;
    }
  });
  const setScriptWidth = (width: number) => {
    setScriptWidthState(width);
    try {
      localStorage.setItem(SCRIPT_WIDTH_STORAGE_KEY, String(width));
    } catch {
      // Preference only.
    }
  };

  // Per-board-view camera (F-121) — view state only, never CRDT.
  const canvas = useMemo(() => createCanvasStore(), []);
  // Comments UI (F-50..F-55) — shared by the top bar, the canvas layers and
  // the sidebar panel. Local view state only (threads live in the Yjs doc).
  const commentsUi = useMemo(() => createCommentsUiStore(), []);
  // F-90 import — shared by this top bar's picker and the canvas's drop layer.
  const importUi = useMemo(() => createImportUiStore(), []);
  // Draw tool — shared by this top bar's toggle and the canvas's capture layer.
  const drawUi = useMemo(() => createDrawUiStore(), []);
  // Design theme — shared by this top bar's toggle, the board band and the panel.
  const designUi = useMemo(() => createDesignUiStore(), []);
  const importPhase = useStore(importUi, (s) => s.phase);
  const panelOpen = useStore(commentsUi, (s) => s.panelOpen);
  // `?thread=<id>` (a notification click, F-110): open that thread and pan its
  // pin into view once the doc has synced far enough to hold it.
  useThreadDeepLink(session, canvas, commentsUi);
  const designPanelOpen = useStore(designUi, (s) => s.panelOpen);
  // Thumbnails (F-130) — shared by this top bar's toggle and the panel; the
  // `?panel=thumbnails` link from the rainvic Projects page opens it.
  const thumbUi = useMemo(() => createThumbUiStore(), []);
  const thumbPanelOpen = useStore(thumbUi, (s) => s.panelOpen);
  useThumbnailDeepLink(thumbUi);
  // Board agent (F-144) — shared by this top bar's toggle and the panel. The
  // store outlives the panel so an applied plan keeps its Revert while the
  // board view is open, even with the panel closed in between.
  const agentUi = useMemo(
    () => createAgentUiStore(storedModel(AGENT_FUNCTION_KEY), loadImportTasks(session.boardId)),
    [],
  );
  const agentPanelOpen = useStore(agentUi, (s) => s.panelOpen);
  // A board created with a file attached imports it on first open.
  useAgentImportOnOpen(session, agentUi);
  // "Divide into sections" and "Translate" are icons in the right pill (the
  // first used to be a "⋯" menu entry; the second only existed on the toolbar
  // of a selected target column). Both are Grid-only: their panels and
  // dialogs mount alongside the grid, so in Script view the icons stay put
  // but disabled, with the hover label saying why. The sections icon toggles
  // its panel; the translate icon opens the dialog with a target picker
  // (features/translate — an existing column, or a new one named after the
  // language).
  const bgmTarget = useStore(bgmUi, (s) => s.target);
  const bgmOpen = bgmTarget?.boardId === session.boardId;
  const sectionsTarget = useStore(sectionsUi, (s) => s.target);
  const sectionsOpen = sectionsTarget !== null && sectionsTarget.boardId === session.boardId;
  // Inside the Rainvic Tool desktop app rather than a browser (core/host.ts).
  // The host is installed before the first render and never swapped, so this
  // is a constant for the life of the screen.
  const embedded = getHost() !== null;
  const hasSourceColumn = sheetId !== null && session.board.sourceColumnId(sheetId) !== undefined;

  // F-138: the storyboard sync folder — a desktop-only host capability (the
  // web shell has nowhere to write). The hook is a no-op without it.
  const storyboardHost = getHost()?.storyboardSync;
  const storyboardEnabled = useStoryboardSyncEnabled(session.boardId);
  const storyboardStatus = useStoryboardSyncStatus(session.boardId);
  useStoryboardSync(
    session,
    boardName,
    commentsUi,
    sheetId,
    summary?.kind === "shorts" ? summary.shorts : undefined,
  );
  const pickStoryboardFolder = async (): Promise<string | null> => {
    if (storyboardHost === undefined) return null;
    const dir = await storyboardHost.pickFolder();
    if (dir !== null) storyboardSync.getState().setRootDir(dir);
    return dir;
  };
  const toggleStoryboardSync = () => {
    if (storyboardHost === undefined) return;
    if (storyboardEnabled) {
      storyboardSync.getState().setEnabled(session.boardId, false);
      showToast("Storyboard sync is off for this board");
      return;
    }
    void (async () => {
      const root = storyboardSync.getState().rootDir ?? (await pickStoryboardFolder());
      if (root === null) return; // cancelled the folder dialog
      storyboardSync.getState().setEnabled(session.boardId, true);
      showToast("Storyboard syncs to your folder while this board is open");
    })();
  };
  const changeStoryboardFolder = () => {
    void pickStoryboardFolder().then((dir) => {
      if (dir !== null) showToast(`Storyboards now sync to ${dir}`);
    });
  };
  const storyboardSubline = (() => {
    if (!storyboardEnabled) return "Off";
    if (storyboardStatus === undefined || storyboardStatus.phase === "idle") return "On";
    if (storyboardStatus.phase === "syncing") return "Syncing…";
    if (storyboardStatus.phase === "error")
      return `Failed: ${storyboardStatus.message ?? "unknown error"}`;
    const where = storyboardStatus.dir ?? storyboardSync.getState().rootDir ?? "";
    return storyboardStatus.message !== undefined
      ? `${storyboardStatus.message} — ${where}`
      : `Synced — ${where}`;
  })();
  const scriptToolsReady = hasSourceColumn && viewMode === "grid";
  const openTranslate = () => {
    if (sheetId === null) return;
    translateUi.getState().openDialog({ boardId: session.boardId, sheetId, colId: null });
  };
  // Shortcut hints in the "⋯" menu, in the platform's own notation.
  const undoKbd = isMac ? "⌘Z" : "Ctrl+Z";
  const redoKbd = isMac ? "⇧⌘Z" : "Ctrl+Shift+Z";
  const toggleSections = () => {
    if (sheetId === null) return;
    if (sectionsOpen) sectionsUi.getState().close();
    else sectionsUi.getState().open({ boardId: session.boardId, sheetId });
  };

  // Undo/redo deliberately do NOT require sheetId: the session-lifetime
  // UndoManager captures pre-sync floating-element edits (session.ts
  // constructor note), so the buttons must work while the grid still loads.
  const canUndo = !frozen && authError === null;

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const exportCsv = () => {
    if (sheetId === null) return;
    const safeName = boardName.replaceAll(/[\\/:*?"<>|]/g, "_") || "board";
    downloadCsv(`${safeName}.csv`, sheetToCsv(session.board, sheetId));
  };

  /**
   * The script, with a blank line between every line, onto the clipboard —
   * ready to paste into a teleprompter app, which reads a blank line as the
   * cue break (see script/teleprompter.ts).
   *
   * READ-ONLY, deliberately: the blank lines exist only on the clipboard.
   * The board keeps the script exactly as written, so the storyboard, the
   * grid and every export are untouched by this.
   *
   * Desktop-only (`embedded`): the prompter sits next to the app the script
   * is read from, and the web shell has no such workflow — so the entry
   * doesn't appear there at all rather than appearing and doing nothing.
   */
  const copyForTeleprompter = () => {
    if (sheetId === null) return;
    const colId = session.board.sourceColumnId(sheetId);
    if (colId === undefined) return;
    const text = scriptToTeleprompterText(
      session.board
        .sortedRowIds(sheetId)
        .map((rowId) => session.board.getCellText(sheetId, rowId, colId)),
    );
    if (text === "") {
      showToast("The script is empty");
      return;
    }
    void copyToClipboard(text).then((copied) => {
      showToast(copied ? "Script copied for teleprompter" : "Couldn't reach the clipboard");
    });
  };

  // A row counts as empty only when EVERY column's cell is blank — a lone
  // space bar doesn't count as content, so cells are trimmed before checking.
  const deleteEmptyRows = () => {
    if (sheetId === null) return;
    const colIds = session.board.sortedColIds(sheetId);
    const emptyRowIds = session.board
      .sortedRowIds(sheetId)
      .filter((rowId) =>
        colIds.every((colId) => session.board.getCellText(sheetId, rowId, colId).trim() === ""),
      );
    if (emptyRowIds.length === 0) {
      showToast("No empty rows to delete");
      return;
    }
    session.deleteRows(sheetId, emptyRowIds);
    showToast(`Deleted ${emptyRowIds.length} empty row${emptyRowIds.length > 1 ? "s" : ""}`);
  };

  /**
   * The right bar's board tools, declared ONCE: rendered as icon buttons on a
   * wide viewport and as rows of the collapsed "⋯" menu on a narrow one. Two
   * hand-written copies would drift the moment a tool gains a disabled rule.
   */
  const boardTools = [
    {
      key: "sections",
      icon: iconSections,
      label: "Divide into sections",
      tip:
        viewMode === "grid"
          ? "Divide into sections — AI groups the script by topic"
          : "Switch to Grid to divide the script into sections",
      disabled: !scriptToolsReady,
      active: sectionsOpen,
      onSelect: toggleSections,
    },
    {
      key: "translate",
      icon: iconTranslate,
      label: "Translate",
      tip:
        viewMode === "grid"
          ? "Translate Original Script into another column"
          : "Switch to Grid to translate the script",
      disabled: !scriptToolsReady,
      active: false,
      onSelect: openTranslate,
    },
    {
      key: "design",
      icon: iconDesign,
      label: "Design theme",
      tip: "Design theme — art direction for this video",
      disabled: false,
      active: designPanelOpen,
      onSelect: () => designUi.getState().setPanelOpen(!designPanelOpen),
    },
    {
      key: "thumbnails",
      icon: iconThumbnail,
      // F-142: a Shorts board gets vertical cover frames.
      label: summary?.kind === "shorts" ? "Cover frames" : "Thumbnails",
      tip:
        summary?.kind === "shorts"
          ? "Cover frames — AI 9:16 cover frames for this Short"
          : "Thumbnails — AI thumbnails for this video",
      disabled: false,
      active: thumbPanelOpen,
      onSelect: () => thumbUi.getState().setPanelOpen(!thumbPanelOpen),
    },
    {
      key: "agent",
      icon: iconAgent,
      label: "Agent",
      tip: "Agent — ask for edits, import a storyboard; every plan is reviewable and revertable",
      disabled: false,
      active: agentPanelOpen,
      onSelect: () => agentUi.getState().setPanelOpen(!agentPanelOpen),
    },
    {
      key: "comments",
      icon: iconPanel,
      label: "Comments",
      tip: "Comments — all threads on this board",
      disabled: false,
      active: panelOpen,
      onSelect: () => commentsUi.getState().setPanelOpen(!panelOpen),
    },
  ];

  return (
    <>
      {/* These rows take up their own space, including when embedded in a
          narrow desktop pane. Shorts and pen controls never share a position. */}
      <div
        className="board-header"
        ref={headerRef}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          setTopbarMenuOpen(false);
          setToolsMenuOpen(false);
          setStageMenuOpen(false);
        }}
      >
        <div className="board-bars">
          <div className="board-bar board-bar-left">
            <div className="topbar-menu-wrap" ref={topbarMenuRef}>
              <button
                type="button"
                className="topbar-menu-btn"
                aria-label="Board menu"
                data-tip="Board menu"
                aria-haspopup="menu"
                aria-expanded={topbarMenuOpen}
                onClick={() => setTopbarMenuOpen((open) => !open)}
              >
                {iconDots}
              </button>
              {topbarMenuOpen &&
                menuTarget !== null &&
                createPortal(
                  <div className="topbar-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        navigate("/");
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconBack}</span>
                      Back to boards
                    </button>
                    <div className="topbar-menu-separator" />
                    <div className="topbar-menu-segmented" role="group" aria-label="View">
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={viewMode === "grid"}
                        className={`topbar-segment${viewMode === "grid" ? " active" : ""}`}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          chooseView("grid");
                        }}
                      >
                        <span className="topbar-segment-icon">{iconGrid}</span>
                        Grid
                      </button>
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={viewMode === "script"}
                        className={`topbar-segment${viewMode === "script" ? " active" : ""}`}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          chooseView("script");
                        }}
                      >
                        <span className="topbar-segment-icon">{iconScript}</span>
                        Script
                      </button>
                    </div>
                    {viewMode === "script" && (
                      <label className="topbar-menu-slider-row" title="Script page width">
                        <span>Width</span>
                        <input
                          type="range"
                          min={SCRIPT_WIDTH_MIN}
                          max={SCRIPT_WIDTH_MAX}
                          step={20}
                          value={scriptWidth}
                          onChange={(event) => setScriptWidth(Number(event.target.value))}
                        />
                      </label>
                    )}
                    <div className="topbar-menu-separator" />
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      disabled={!canUndo}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        session.undo();
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconUndo}</span>
                      Undo
                      <kbd className="topbar-menu-item-kbd">{undoKbd}</kbd>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      disabled={!canUndo}
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        session.redo();
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconRedo}</span>
                      Redo
                      <kbd className="topbar-menu-item-kbd">{redoKbd}</kbd>
                    </button>
                    <div className="topbar-menu-separator" />
                    {/* Board-level actions. "Copy share link" lives HERE rather than
                      as a floating button over the canvas: it is a property of the
                      board, like renaming or exporting it, and the desktop app's
                      own floating "⋯" over the embedded Kola view was removed in
                      favour of this one (rainvic-tool components/Kola/KolaView). */}
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      title="Copy a link that opens this board in the app"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        void copyToClipboard(boardShareLink(session.boardId)).then((copied) => {
                          showToast(copied ? "Share link copied" : "Couldn't reach the clipboard");
                        });
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconLink}</span>
                      Copy share link
                    </button>
                    {/* F-123: the same link, plus exactly this camera — what a
                      teammate should be looking at, not just which board. */}
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      disabled={viewMode !== "grid"}
                      title={
                        viewMode === "grid"
                          ? "Copy a link that opens this board at exactly this position and zoom"
                          : "Switch to Grid to copy a link to a position on the board"
                      }
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        const link = boardViewShareLink(session.boardId, canvas.getState().camera);
                        void copyToClipboard(link).then((copied) => {
                          showToast(
                            copied ? "Link to this view copied" : "Couldn't reach the clipboard",
                          );
                        });
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconLink}</span>
                      Copy link to this view
                    </button>
                    <div className="topbar-menu-separator" />
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      disabled={sheetId === null || frozen || importBusy(importPhase)}
                      title="Import a script (.txt, .csv, .tsv) into the Original Script column"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        fileInputRef.current?.click();
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconImport}</span>
                      Import script…
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      disabled={sheetId === null}
                      title="Export the grid as CSV"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        exportCsv();
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconExport}</span>
                      Export CSV
                    </button>
                    {embedded && (
                      <button
                        type="button"
                        role="menuitem"
                        className="topbar-menu-item"
                        disabled={!hasSourceColumn}
                        title="Copy the script with a blank line between every line, for a teleprompter. The board itself is not changed."
                        onPointerDown={(event) => {
                          event.preventDefault();
                          setTopbarMenuOpen(false);
                          copyForTeleprompter();
                        }}
                      >
                        <span className="topbar-menu-item-icon">{iconTeleprompter}</span>
                        Copy script for teleprompter
                      </button>
                    )}
                    {storyboardHost !== undefined && (
                      <>
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={storyboardEnabled}
                          className="topbar-menu-item"
                          title="Keep a folder on this computer current with this board's storyboard — the script with its sound and visual cues, every image, video, file and link with its clip range — for an AI agent to read. Written while the board is open here."
                          onPointerDown={(event) => {
                            event.preventDefault();
                            setTopbarMenuOpen(false);
                            toggleStoryboardSync();
                          }}
                        >
                          <span className="topbar-menu-item-icon">{iconSyncFolder}</span>
                          {storyboardEnabled
                            ? "Stop syncing storyboard for AI"
                            : "Sync storyboard for AI"}
                          <span className="topbar-menu-item-sub">{storyboardSubline}</span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="topbar-menu-item"
                          title="Choose the folder storyboards are written into (one sub-folder per board)"
                          onPointerDown={(event) => {
                            event.preventDefault();
                            setTopbarMenuOpen(false);
                            changeStoryboardFolder();
                          }}
                        >
                          <span className="topbar-menu-item-icon">{iconSyncFolder}</span>
                          Change storyboard folder…
                        </button>
                      </>
                    )}
                    <div className="topbar-menu-separator" />
                    <button
                      type="button"
                      role="menuitem"
                      className="topbar-menu-item"
                      disabled={sheetId === null || frozen}
                      title="Delete every row whose cells are all blank"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        setTopbarMenuOpen(false);
                        deleteEmptyRows();
                      }}
                    >
                      <span className="topbar-menu-item-icon">{iconRowsClear}</span>
                      Delete empty rows
                    </button>
                  </div>,
                  menuTarget,
                )}
            </div>
            <div className="board-identity">
              <div
                className="board-title"
                title={boardName}
                onDoubleClick={() => {
                  if (summary !== null) setRenamingBoard(true);
                }}
              >
                {boardName}
              </div>
              <div className="board-metadata">
                {summary !== null && (
                  <div className="board-card-menu-wrap">
                    <button
                      type="button"
                      className={`status-chip status-${summary.status} status-chip-btn`}
                      title="Change stage (assigns the next owner and creates a task)"
                      aria-label="Change stage"
                      aria-haspopup="menu"
                      aria-expanded={stageMenuOpen}
                      onClick={() => setStageMenuOpen((open) => !open)}
                      onBlur={() => setStageMenuOpen(false)}
                    >
                      {BOARD_STATUS_LABELS[summary.status]} ▾
                    </button>
                    {stageMenuOpen &&
                      menuTarget !== null &&
                      createPortal(
                        <div className="board-card-menu" role="menu">
                          {/* The decision first, on a board that is waiting for it:
                        a project manager who opened the board to READ the
                        storyboard should not have to leave for the dashboard to
                        act on it. */}
                          {canApprove && summary.status === "request_review" && (
                            <>
                              <button
                                type="button"
                                role="menuitem"
                                className="board-card-menu-item"
                                onPointerDown={(event) => {
                                  event.preventDefault();
                                  setStageMenuOpen(false);
                                  setSigningOff("approve");
                                }}
                              >
                                Approve storyboard…
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                className="board-card-menu-item"
                                onPointerDown={(event) => {
                                  event.preventDefault();
                                  setStageMenuOpen(false);
                                  setSigningOff("send_back");
                                }}
                              >
                                Send storyboard back…
                              </button>
                              <div className="board-card-menu-separator" />
                            </>
                          )}
                          {BOARD_STATUSES.filter((s) => s !== summary.status).map((s) => (
                            <button
                              key={s}
                              type="button"
                              role="menuitem"
                              className="board-card-menu-item"
                              onPointerDown={(event) => {
                                event.preventDefault();
                                setStageMenuOpen(false);
                                setStageTarget(s);
                              }}
                            >
                              Move to {BOARD_STATUS_LABELS[s]}
                            </button>
                          ))}
                        </div>,
                        menuTarget,
                      )}
                  </div>
                )}
                {/* F-137: the script deadline, on the board itself while the script
                is being written — the same chip as the dashboard card, moving
                the same date through the same route. */}
                {summary !== null &&
                  summary.status === "scripting" &&
                  summary.scriptFinishDate !== null && (
                    <DateButton
                      value={summary.scriptFinishDate}
                      onChange={(dateKey) => void handleSetScriptDue(dateKey)}
                      placeholder="Set script due date"
                      prefix="Script due"
                      context={summary.name}
                      className={`date-button-chip topbar-script-due${isPastDateKey(summary.scriptFinishDate) ? " date-button-chip-overdue" : ""}`}
                    />
                  )}
              </div>
            </div>
            {summary !== null && (
              <button
                type="button"
                className={`board-music-button${bgmOpen ? " active" : ""}`}
                disabled={sheetId === null}
                aria-label="Music details"
                aria-expanded={bgmOpen}
                aria-controls="board-music-details"
                title="Music — expand BGM details"
                onClick={() => {
                  if (bgmOpen) bgmUi.getState().close();
                  else if (sheetId !== null) {
                    chooseView("grid");
                    sectionsUi.getState().close();
                    bgmUi.getState().open({ boardId: session.boardId, sheetId });
                  }
                }}
              >
                {iconMusic}
                <span>Music</span>
              </button>
            )}
            {/* F-90: the picker for the menu's "Import script…" item; hands the
                file to the same store the canvas's drop target feeds. */}
            <input
              ref={fileInputRef}
              type="file"
              accept={IMPORT_ACCEPT}
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Reset first: picking the SAME file twice fires no change event
                // otherwise, so a failed import could never be retried.
                event.target.value = "";
                if (file !== undefined) void beginImport(importUi, file);
              }}
            />
          </div>
          <div className="board-bar board-bar-right">
            {narrow ? (
              <div className="topbar-menu-wrap" ref={toolsMenuRef}>
                <button
                  type="button"
                  className={`topbar-menu-btn${boardTools.some((tool) => tool.active) ? " active" : ""}`}
                  aria-label="Board tools"
                  data-tip="Board tools"
                  aria-haspopup="menu"
                  aria-expanded={toolsMenuOpen}
                  onClick={() => setToolsMenuOpen((open) => !open)}
                >
                  {iconDots}
                </button>
                {toolsMenuOpen &&
                  menuTarget !== null &&
                  createPortal(
                    <div className="topbar-menu topbar-menu-end" role="menu">
                      {boardTools.map((tool) => (
                        <button
                          key={tool.key}
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={tool.active}
                          className={`topbar-menu-item${tool.active ? " active" : ""}`}
                          disabled={tool.disabled}
                          title={tool.tip}
                          onPointerDown={(event) => {
                            event.preventDefault();
                            setToolsMenuOpen(false);
                            tool.onSelect();
                          }}
                        >
                          <span className="topbar-menu-item-icon">{tool.icon}</span>
                          {tool.label}
                        </button>
                      ))}
                    </div>,
                    menuTarget,
                  )}
              </div>
            ) : (
              boardTools.map((tool) => (
                <button
                  key={tool.key}
                  type="button"
                  className={`icon-btn${tool.active ? " active" : ""}`}
                  disabled={tool.disabled}
                  onClick={tool.onSelect}
                  aria-label={tool.label}
                  data-tip={tool.tip}
                  aria-pressed={tool.active}
                >
                  {tool.icon}
                </button>
              ))
            )}
            <NotificationsBell />
            <ThemeToggle />
            <AvatarStack self={session.user} peers={peers} />
            <div className="status-pill">
              <span className={`status-dot ${status}`} />
              {status === "connected" ? "Live" : status === "connecting" ? "Connecting" : "Offline"}
            </div>
          </div>
        </div>
        <div
          className="board-menu-slot"
          data-align={toolsMenuOpen ? "end" : "start"}
          ref={setMenuTarget}
        />
        <div className="board-secondary-controls">
          {/* F-141: the Shorts rail — one chip per short, in release order. */}
          {summary !== null && summary.kind === "shorts" && summary.shorts.length > 0 && (
            <ShortsRail
              session={session}
              summary={summary}
              sheetId={sheetId}
              gridActive={viewMode === "grid"}
              onPatch={(patches, after, toast) => void handlePlanPatch(patches, after, toast)}
              onJump={handleJumpToRow}
            />
          )}
          {viewMode === "grid" && <DrawToolbar draw={drawUi} />}
        </div>
      </div>
      <ConnectionBanner status={status} frozen={frozen} authError={authError} />
      <div className="board-main">
        {viewMode === "script" ? (
          <ScriptView session={session} pageWidth={scriptWidth} summary={summary} />
        ) : (
          <BoardCanvas
            session={session}
            canvas={canvas}
            commentsUi={commentsUi}
            importUi={importUi}
            drawUi={drawUi}
            designUi={designUi}
          />
        )}
        {agentPanelOpen && <AgentPanel session={session} ui={agentUi} />}
        {panelOpen && <CommentsPanel session={session} canvas={canvas} ui={commentsUi} />}
        {designPanelOpen && <DesignPanel session={session} ui={designUi} />}
        {thumbPanelOpen && (
          <ThumbnailPanel
            session={session}
            canvas={canvas}
            ui={thumbUi}
            summary={summary}
            onShowGrid={() => chooseView("grid")}
          />
        )}
      </div>
      {stageTarget !== null && summary !== null && (
        <AssignStatusDialog
          board={summary}
          toStatus={stageTarget}
          onClose={() => setStageTarget(null)}
          onDone={(response) => {
            setStageTarget(null);
            onSummaryChange(response.board);
            if (response.taskCreated) {
              showToast(`Moved to ${BOARD_STATUS_LABELS[response.board.status]} — task created`);
            } else if (response.taskError !== undefined) {
              showToast("Stage changed, but creating the task failed — check the task board");
            }
          }}
        />
      )}
      {signingOff !== null && summary !== null && (
        <StoryboardGateDialog
          board={summary}
          action={signingOff}
          onClose={() => setSigningOff(null)}
          onDone={(response) => {
            setSigningOff(null);
            // The board MOVED — approving sends it to Shooting, rejecting back
            // to Storyboarding — so the stage chip has to follow, not just the
            // approval timestamp.
            onSummaryChange(response.board);
            showToast(
              response.autoAdvanced
                ? "Storyboard approved — the board moved to Shooting"
                : "Storyboard sent back",
            );
          }}
        />
      )}
      {renamingBoard && summary !== null && (
        <RenameBoardDialog
          initial={summary.name}
          onClose={() => setRenamingBoard(false)}
          onSubmit={(name) => {
            setRenamingBoard(false);
            const trimmed = name.trim();
            if (trimmed === "" || trimmed === summary.name) return;
            renameBoard(session.boardId, trimmed)
              .then(onSummaryChange)
              .catch((err: unknown) => {
                showToast(err instanceof Error ? err.message : "Rename failed");
              });
          }}
        />
      )}
    </>
  );
}

/** Double-click-the-title rename dialog (mirrors GridTable's column rename). */
function RenameBoardDialog({
  initial,
  onSubmit,
  onClose,
}: {
  initial: string;
  onSubmit: (name: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <Modal title="Rename board" onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(value);
        }}
        style={{ display: "flex", flexDirection: "column", gap: 12 }}
      >
        <input
          className="text-input"
          autoFocus
          placeholder="Board name"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          maxLength={200}
        />
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary">
            Rename
          </button>
        </div>
      </form>
    </Modal>
  );
}
